import React from 'react';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { Dispatch } from 'redux';
import { AppState } from 'acscp/store';
import { FetchRequest } from 'acscp/actions';
import style from './accountLinks.module.scss';
import { FormattedMessage } from 'react-intl';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { AccountLinksRequest, DeleteAccountLinkRequest, RequestClass } from 'acscp/api/requests';
import { RequestStateProps } from '../common/helpers/requestStateProps';
import { LoadingIndicator } from '../common/loadingIndicator';
import { AccountLink as AccountLinkModel } from 'acscp/api/models/accountLink';
import { AccountLink } from './accountLink';
import { userCanAccessCharters } from 'acscp/store/helpers/authHelper';

interface AccountLinksStateProps extends RequestStateProps {
    accountLinks: AccountLinkModel[];
    canAccessCharters: boolean;
}

interface AccountLinksDispatchProps {
    fetchAccountLinks: () => void;
    deleteAccountLink: (emailAddress: string) => void;
}

export class AccountLinksPresentational extends PresentationComponentBase<
    {}, AccountLinksStateProps, AccountLinksDispatchProps, {}> {

    componentDidMount(): void {
        this.props.fetchAccountLinks();
    }

    render(): JSX.Element | null {
        const { accountLinks, canAccessCharters, isLoading, requestError } = this.props;

        if (!canAccessCharters) {
            return null;
        }
        if (isLoading) {
            return (<LoadingIndicator />);
        }
        if (requestError) {
            return(
                <div className={style.Info}>
                    <FormattedMessage
                        id={'components.accountLinks.failedToLoadLinkedAccounts'}
                        defaultMessage={'Failed to load linked accounts.'}
                    />
                </div>
            );
        }
        if (accountLinks.length === 0) {
            return (
                <div className={style.Info}>
                    <FormattedMessage
                        id={'components.accountLinks.noLinkedAccountsMessage'}
                        defaultMessage={'You currently have no linked accounts.'}
                    />
                </div>
            );
        }
        return (
            <div>
                {accountLinks.map(accountLink =>
                    <AccountLink
                        key={accountLink.emailAddress}
                        accountLink={accountLink}
                        deleteAccountLink={() => this.props.deleteAccountLink(accountLink.emailAddress)}
                    />
                )}
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): AccountLinksStateProps {
    const accountLinksError = getRequestError(AccountLinksRequest);
    const deleteAccountError = getRequestError(DeleteAccountLinkRequest);
    return {
        isLoading: accountLinksError.isLoading || deleteAccountError.isLoading,
        success: accountLinksError.success,
        requestError: accountLinksError.requestError,
        accountLinks: state.accountLinks.accountLinks,
        canAccessCharters: userCanAccessCharters(state.auth)
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): AccountLinksDispatchProps {
    return {
        fetchAccountLinks: () => dispatch(new FetchRequest(new AccountLinksRequest())),
        deleteAccountLink: (emailAddress: string) => dispatch(new FetchRequest(new DeleteAccountLinkRequest(emailAddress)))
    };
}

export const AccountLinks = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (AccountLinksPresentational);
